import {computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../components/toast/ErrorToast";
import successToast from "../../components/toast/SuccessToast";
import {DELIVERY_SITE_ONBOARDING_CREATE_PATH, DELIVERY_SITE_ONBOARDING_EDIT_PATH} from "../../routes";
import TaxProfileForm from "../../domain/TaxProfileForm";
import EmailField from "../../forms/fields/EmailField";

export default class CustomerFeature {

    constructor(application, navigator, id, customer) {
        this._application = application;
        this._navigate = navigator;
        this._loaded = false;
        this._businessName = id;
        this._customer = customer;
        this._deliverySiteOnboardings = [];
        this._taxProfileForm = null;
        this._hasMultipleJurisdictions = false;
        this._buttonLoading = false;
        this._collectionsEmailField = null;

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _customer: observable,
            customer: computed,
            _collectionsEmailField: observable,
            _deliverySiteOnboardings: observable,
            deliverySiteOnboardings: computed,
            _taxProfileForm: observable,
            taxProfileForm: computed,
            _hasMultipleJurisdictions: observable,
            hasMultipleJurisdictions: computed,
            hasCm05Form: computed,
            _buttonLoading: observable,
            buttonLoading: computed,
        })
    }

    async aValueChanged() {
    }

    async load() {
        await this.customerDeliverySiteOnboardings()
        await this.loadGrossIncomeTaxProfile()
        await this.loadCollectionsEmail()
        this.finishLoading()
    }

    async reload() {
        this._loaded = false;
        await this.load();
    }

    finishLoading() {
        this._loaded = true
    }

    get loaded() {
        return this._loaded;
    }

    set loaded(value) {
        this._loaded = value;
    }

    get customer() {
        return this._customer;
    }

    set customer(value) {
        this._customer = value;
    }

    get deliverySiteOnboardings() {
        return this._deliverySiteOnboardings;
    }

    set deliverySiteOnboardings(value) {
        this._deliverySiteOnboardings = value;
    }

    get taxProfileForm() {
        return this._taxProfileForm;
    }

    set taxProfileForm(value) {
        this._taxProfileForm = value;
    }

    get hasCm05Form() {
        return !!this._taxProfileForm.cm05form()?.name;
    }

    get hasMultipleJurisdictions() {
        return this._hasMultipleJurisdictions;
    }

    set hasMultipleJurisdictions(value) {
        this._hasMultipleJurisdictions = value;
    }

    taxpayerIdentificationNumber(){
        return this._customer.taxpayerIdentificationNumber
    }

    businessName(){
        return this._customer.businessName
    }

    fantasyName(){
        return this._customer.fantasyName
    }

    customerCode(){
        return this._customer.code
    }

    franchiseBrandName(){
        return this._customer.franchiseBrandName
    }

    collectionsEmailField(){
        return this._collectionsEmailField
    }

    async loadCollectionsEmail() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._collectionsEmailField = new EmailField(true, response.collectionsEmail());
            },
            handlesError: () => {
                this._collectionsEmailField = new EmailField(true);
            }
        });
        return this._application.apiClient().customerContacts(responseHandler, this._businessName);
    }

    editCollectionsEmail(collectionsEmail) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: () => {
                successToast("Mail de cobranzas actualizado");
            },
            handlesError: () => {
                errorToast("No se pudo actualizar el mail de cobranzas");
            }
        });
        return this._application.apiClient().editCustomerCollectionsEmail(
            responseHandler,
            this.taxpayerIdentificationNumber(),
            collectionsEmail
        );
    }

    async customerDeliverySiteOnboardings() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this.deliverySiteOnboardings = response.deliverySiteOnboardings();
            },
            handlesError: () => {
                errorToast("No se pudieron obtener las altas de puntos de entrega");
            }
        });
        return this._application.apiClient().customerDeliverySiteOnboardings(
            responseHandler,
            this.taxpayerIdentificationNumber()
        );
    }

    navigateToCreateDeliverySiteOnboarding() {
        const path = DELIVERY_SITE_ONBOARDING_CREATE_PATH.replace(':id', this._businessName)
        this._navigate(path)
    }

    get buttonLoading() {
        return this._buttonLoading;
    }

    set buttonLoading(value) {
        this._buttonLoading = value;
    }

    synchronizeERP() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: () => {
                this._simulateProcessingDelay()
                successToast('Sincronización finalizada con éxito')
            },
            handlesError: () => {
                this._simulateProcessingDelay()
                errorToast('Fallo en la sincronización')
            }
        });
        this._disableButton()
        return this._application.apiClient().synchronizeWithERP(responseHandler)
    }

    _simulateProcessingDelay() {
        setTimeout(() => {
            this._enableButton();
        }, 3000);
    }

    _disableButton() {
        this._buttonLoading = true;
    }

    _enableButton() {
        this._buttonLoading = false;
    }

    deliverySiteOnboardingsColumns() {
        return [
            {id: 'name', label: 'Punto de Entrega'},
            {id: 'address', label: 'Dirección'},
            {id: 'city', label: 'Ciudad'},
            {id: 'postalCode', label: 'Código Postal'},
            {id: 'state', label: 'Estado'},
            {id: 'requester', label: 'Solicitante'},
            {id: 'requestTimestamp', label: 'Fecha de Solicitud'},
            {id: 'taxAuthorization', label: 'Impositiva'},
            {id: 'logisticsAuthorization', label: 'Logística'},
        ];
    }

    presentedDeliverySiteOnboardings() {
        return this.deliverySiteOnboardings.map(deliverySiteOnboarding =>
            this._presentDeliverySiteOnboarding(deliverySiteOnboarding)
        );
    }

    _presentDeliverySiteOnboarding(deliverySiteOnboarding) {
        return {
            id: deliverySiteOnboarding.id,
            name: deliverySiteOnboarding.request.name,
            address: deliverySiteOnboarding.request.address,
            city: deliverySiteOnboarding.request.city,
            postalCode: deliverySiteOnboarding.request.postalCode,
            state: deliverySiteOnboarding.state,
            requester: deliverySiteOnboarding.requester,
            requestTimestamp: deliverySiteOnboarding.requestTimestamp,
            taxAuthorization: deliverySiteOnboarding.taxAuthorization,
            logisticsAuthorization: deliverySiteOnboarding.logisticsAuthorization,
        };
    }

    navigateToDeliverySiteOnboarding(row) {
        const path = DELIVERY_SITE_ONBOARDING_EDIT_PATH.replace(':id', row.id);
        this._navigate(path);
    }

    _createTaxProfileForm(data) {
        this.taxProfileForm = TaxProfileForm.newFor(data, this);
    }

    async loadGrossIncomeTaxProfile() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this.hasMultipleJurisdictions = response.hasMultipleJurisdictions();
                this._createTaxProfileForm({
                    cm01form: response.cm01Form(),
                    cm05form: response.cm05Form(),
                });
            },
            handlesError: () => {
                this._createTaxProfileForm({});
            }
        });
        return this._application.apiClient().grossIncomeTaxProfileOfCustomer(
            responseHandler,
            this.taxpayerIdentificationNumber()
        );
    }

    async onCm01FormChanged() {
        const value = this.taxProfileForm.cm01form();
        if (value instanceof File) {
            await this._uploadCmForm(value, null, "Formulario CM01 actualizado", "No se pudo actualizar el formulario CM01");
        } else if (!value || !value.name) {
            await this._deleteCm01Form();
        }
    }

    async onCm05FormChanged() {
        const value = this.taxProfileForm.cm05form();
        if (value instanceof File) {
            await this._uploadCmForm(null, value, "Formulario CM05 actualizado", "No se pudo actualizar el formulario CM05");
        } else if (!value || !value.name) {
            await this._deleteCm05Form();
        }
    }

    async _uploadCmForm(cm01Form, cm05Form, successMessage, errorMessage) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._createTaxProfileForm({
                    cm01form: response.cm01Form(),
                    cm05form: response.cm05Form(),
                });
                successToast(successMessage);
            },
            handlesError: () => {
                errorToast(errorMessage);
            }
        });
        return this._application.apiClient().editCm01AndCm05Forms(
            responseHandler,
            this.taxpayerIdentificationNumber(),
            cm01Form,
            cm05Form
        );
    }

    async _deleteCm01Form() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._createTaxProfileForm({
                    cm01form: response.cm01Form(),
                    cm05form: response.cm05Form(),
                });
                successToast("Formulario CM01 eliminado");
            },
            handlesError: () => {
                errorToast("No se pudo eliminar el formulario CM01");
            }
        });
        return this._application.apiClient().deleteCm01Form(
            responseHandler,
            this.taxpayerIdentificationNumber()
        );
    }

    async _deleteCm05Form() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._createTaxProfileForm({
                    cm01form: response.cm01Form(),
                    cm05form: response.cm05Form(),
                });
                successToast("Formulario CM05 eliminado");
            },
            handlesError: () => {
                errorToast("No se pudo eliminar el formulario CM05");
            }
        });
        return this._application.apiClient().deleteCm05Form(
            responseHandler,
            this.taxpayerIdentificationNumber()
        );
    }
}
